import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  IconSettings,
  IconFilePlus,
  IconFileText,
  IconChartAreaLine,
  IconChevronRight,
  IconDotsCircleHorizontal,
  IconFlag,
} from '@tabler/icons';

import api from '../../services/ApiManagement/Index';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { useOnlineStatus } from '../../hooks/onlineStatus';
import getAnimalStatusColor from '../../utils/getAnimalStatusColor';

import BackHeader from '../../components/BackHeader';
import AnimalItem from '../../components/Animal/SmallItem';
import AlertItem from '../../components/Animal/AlertItem';
import ImageModal from '../../components/ImageModal';
import Button from '../../components/Button';
import TextArea from '../../components/Form/TextArea';
import RadioInput from '../../components/Form/RadioInput';
import Modal from '../../components/Modal';
import PrivateMessage from '../../components/PrivateMessage';
import LoadingComponent from './components/LoadingComponent';
import { Row, Divider, Label, H2, Text } from '../../components/Generic';
import {
  AnimalImage,
  Container,
  Content,
  Item,
  Grid,
  IconContainer,
  NavBar,
  SmallButton,
  Section,
  Obs,
  ParentsRow,
  CustomGraphTooltip,
  Breadcumb,
  SeeMoreButton,
} from './styles';

import animalImg from '../../assets/images/animal-example.png';
import RegisterItem from '../../components/RegisterItem';
import StatusSelect from './components/StatusSelect';
import Input from '../../components/Form/Input';

const Animal: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('registers');
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [animal, setAnimal] = useState<any>(null);
  const [weights, setWeights] = useState<any>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [newAnimalState, setNewAnimalState] = useState();

  const history = useHistory();
  const isOnline = useOnlineStatus();
  const { id_property, id_animal }: any = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const alertFormRef = useRef<FormHandles>(null);

  useEffect(() => {
    fetchData();
  }, [id_animal]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const animalResponse = await api.get(
        `/animais/${id_animal}`,
        isOnline,
        'animal',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      setAnimal(animalResponse.data);
      console.log(animalResponse.data);

      if (
        animalResponse.data?.ultimosRegistros?.inseminacao?.animalCategoria_id
      ) {
        const inseminacaoAnimalResponse = await api.get(
          `/animais/${animalResponse.data.ultimosRegistros.inseminacao.animalCategoria_id}`,
          isOnline,
          'animal',
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );

        setAnimal({
          ...animalResponse.data,
          ultimosRegistros: {
            ...animalResponse.data.ultimosRegistros,
            inseminacao: {
              ...animalResponse.data.ultimosRegistros.insemincacao,
              animalCategoria: inseminacaoAnimalResponse.data,
            },
          },
        });
      }

      const weigthsResponse = await api.get(
        `/registros/todos/${id_animal}`,
        isOnline,
        'registros',
        {
          params: {
            keyword: 'peso',
            pagina: 1,
            limite: 10,
          },
        },
      );

      if (weigthsResponse.data) {
        const newWeights = weigthsResponse.data.map(
          (weight: any, index: any) => {
            let gmd = 0;

            if (weigthsResponse.data[index + 1]) {
              const before = moment(weigthsResponse.data[index + 1].data);
              const after = moment(weight.data);
              const diff = after.diff(before, 'days');

              gmd =
                (weight.descricao - weigthsResponse.data[index + 1].descricao) /
                (diff !== 0 ? diff : 1);
            }

            return {
              weight: weight.descricao,
              date: `${moment(weight.data).format('DD/MM/YY')} ${
                weight.rotulo ? '-' : ''
              } ${weight.rotulo || ''}`,
              gmd: Math.round(gmd * 100) / 100,
            };
          },
        );

        setWeights(newWeights.reverse());
      }

      const propertyResponse = await api.get(
        `/propriedades/`,
        isOnline,
        'propriedades',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      if (propertyResponse.data.proprietario_id === user.id) {
        setCanEdit(true);
      } else {
        const userExists = propertyResponse.data.colaboradores.some(
          (el: any) => {
            return el.usuario_id === user.id;
          },
        );
        setCanEdit(userExists);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = async (data: any) => {
    try {
      setLoadingAction(true);

      if (animal.estado === 'ativo') {
        await api.put(
          `/animais/${id_animal}`,
          {
            obs: data.obs,
          },
          isOnline,
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );
      }

      await api.put(
        `/animais/estado/${id_animal}`,
        {
          estado: newAnimalState,
        },
        isOnline,
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      if (animal.estado !== 'ativo') {
        await api.put(
          `/animais/${id_animal}`,
          {
            obs: data.obs,
          },
          isOnline,
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );
      }

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Estado do animal atualizado.',
      });

      history.go(0);
    } catch (err) {
      console.log(err);
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado.',
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleAlertChange = async (data: any) => {
    try {
      setLoadingAction(true);

      alertFormRef.current?.setErrors({});

      const schema = Yup.object().shape({
        titulo: Yup.string().required('Campo obrigatório.'),
        descricao: Yup.string().required('Campo obrigatório.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const toSend =
        data.tipo === 'verde'
          ? {
              alerta_verde: {
                conteudo: `${data.titulo}\n${data.descricao}`,
              },
              alerta_vermelho: {
                conteudo: ``,
              },
            }
          : {
              alerta_vermelho: {
                conteudo: `${data.titulo}\n${data.descricao}`,
              },
              alerta_verde: {
                conteudo: ``,
              },
            };

      await api.put(
        `/animais/${id_animal}`,
        {
          alerta: toSend,
        },
        isOnline,
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Alerta do animal atualizado.',
      });

      history.go(0);
    } catch (err) {
      console.log(err);
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado.',
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const radioOptions = [
    { id: 'verde', value: 'verde', label: 'Verde' },
    { id: 'vermelho', value: 'vermelho', label: 'Vermelho' },
  ];

  return (
    <Container>
      <BackHeader title="Animal">
        {canEdit && (
          <IconContainer
            onClick={() =>
              history.push(
                `/propriedade/${id_property}/animais/${id_animal}/editar/`,
              )
            }
            id="edit-button"
          >
            <IconSettings size={24} />
          </IconContainer>
        )}
      </BackHeader>

      {loading ? (
        <LoadingComponent />
      ) : animal === null ? (
        <Content>
          <PrivateMessage />
        </Content>
      ) : (
        <Content>
          <Breadcumb>
            <span onClick={() => history.go(-2)}>Propriedade</span>

            <IconChevronRight size={15} color="#999" />

            <span onClick={() => history.goBack()}>Pesquisar</span>

            <IconChevronRight size={15} color="#999" />

            <span>Animal</span>
          </Breadcumb>

          {animal.alerta?.alerta_vermelho?.conteudo && (
            <AlertItem
              type="red"
              title={
                animal.alerta.alerta_vermelho.conteudo.split('\n')[0] || '--'
              }
              description={
                animal.alerta.alerta_vermelho.conteudo.split('\n')[1] || '--'
              }
              animalId={animal.id}
              propertyId={id_property}
            />
          )}

          {animal.alerta?.alerta_verde?.conteudo && (
            <AlertItem
              type="green"
              title={animal.alerta.alerta_verde.conteudo.split('\n')[0] || '--'}
              description={
                animal.alerta.alerta_verde.conteudo.split('\n')[1] || '--'
              }
              animalId={animal.id}
              propertyId={id_property}
            />
          )}

          <StatusSelect
            onChange={(e: any) => {
              setNewAnimalState(e.target.value);
              setIsModalOpen(true);
            }}
            animal={animal}
            defaultValue={animal.estado}
            canEdit={canEdit}
          />

          {animal.obs && <Obs>{animal.obs}</Obs>}

          <Grid>
            <AnimalImage>
              <span
                style={{
                  background: getAnimalStatusColor(
                    animal.pertencente,
                    animal.estado,
                    animal.sexo,
                  ),
                }}
              >
                Nº {animal?.numero_de_identificacao || '--'}
              </span>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <img
                src={
                  isOnline
                    ? animal?.imagens?.length > 0
                      ? animal?.imagens[0]?.image_url
                      : animalImg
                    : animalImg
                }
                alt="Animal"
                onClick={() => setIsImageModalOpen(true)}
                style={{
                  cursor: animal?.imagens?.length > 0 ? 'pointer' : 'default',
                }}
              />
            </AnimalImage>

            <Item>
              <span>Botom</span>
              <strong>{animal?.bottom_eletronico || '--'}</strong>
            </Item>

            <Item></Item>

            <Item>
              <span>Nascimento</span>
              <strong>
                {typeof animal.data_de_nascimento === 'number'
                  ? animal.data_de_nascimento
                  : moment(
                      animal.data_de_nascimento
                        .toString()
                        .replace('T00:00:00.000Z', ''),
                    ).format('DD[/]MM[/]YYYY')}
              </strong>
            </Item>

            <Item>
              <span>Sexo</span>
              <strong>{animal?.sexo === 'macho' ? 'Macho' : 'Fêmea'}</strong>
            </Item>

            <Item>
              <span>Nome</span>
              <strong>{animal?.nome || '--'}</strong>
            </Item>

            <Item>
              <span>Raça</span>
              <strong>{animal?.ra_a || '--'}</strong>
            </Item>

            <Item>
              <span>Categoria</span>
              <strong>{animal?.categoria || '--'}</strong>
            </Item>

            <Item>
              <span>Lote</span>
              <strong>{animal?.lote || '--'}</strong>
            </Item>

            <Item>
              <span>Safra</span>
              <strong>{animal?.safra || '--'}</strong>
            </Item>
          </Grid>

          <ParentsRow>
            <a
              href={
                animal.pai
                  ? `/propriedade/${id_property}/animais/${animal.pai.id}`
                  : '#'
              }
            >
              <AnimalItem
                label="Pai"
                imageUrl={animal.pai?.imagens?.[0]?.image_url}
                name={animal.pai?.nome || '--'}
                number={animal.pai?.numero_de_identificacao || '--'}
                belongs={animal.pai?.pertencente}
                state={animal.pai?.estado}
                gender="macho"
              />
            </a>

            <a
              href={
                animal.mae
                  ? `/propriedade/${id_property}/animais/${animal.mae.id}`
                  : '#'
              }
            >
              <AnimalItem
                label="Mãe"
                imageUrl={animal.mae?.imagens?.[0]?.image_url}
                name={animal.mae?.nome || '--'}
                number={animal.mae?.numero_de_identificacao || '--'}
                belongs={animal.mae?.pertencente}
                state={animal.mae?.estado}
                gender="femea"
              />
            </a>
          </ParentsRow>

          <SeeMoreButton
            type="button"
            onClick={() =>
              history.push(
                `/propriedade/${id_property}/animais/${id_animal}/irmaos`,
              )
            }
            style={{ justifyContent: 'flex-start' }}
          >
            <IconDotsCircleHorizontal
              size={20}
              color="#777"
              style={{ marginRight: 10 }}
            />
            Irmãos
          </SeeMoreButton>

          <SeeMoreButton
            type="button"
            onClick={() =>
              history.push(
                `/propriedade/${id_property}/animais/${id_animal}/filhos`,
              )
            }
            style={{ justifyContent: 'flex-start' }}
          >
            <IconDotsCircleHorizontal size={20} color="#777" />
            Filhos
          </SeeMoreButton>

          <Divider />

          <NavBar>
            <SmallButton
              active={selectedTab === 'registers'}
              onClick={() => setSelectedTab('registers')}
            >
              <IconFileText size={25} />
              <span>Registros</span>
            </SmallButton>

            <SmallButton
              active={selectedTab === 'graph'}
              onClick={() => setSelectedTab('graph')}
            >
              <IconChartAreaLine size={25} />
              <span>Gráfico</span>
            </SmallButton>

            <SmallButton
              active={selectedTab === 'alerts'}
              onClick={() => setSelectedTab('alerts')}
            >
              <IconFlag size={25} />
              <span>Alerta</span>
            </SmallButton>
          </NavBar>

          {selectedTab === 'registers' && (
            <Section>
              <Label>Mais recentes</Label>

              {Object.keys(animal?.ultimosRegistros || {})
                ?.map((key: any) => [key, animal.ultimosRegistros[key]])
                .sort((a: any, b: any) => (a[1].data < b[1].data && 1) || -1)
                .map((item: any) => (
                  <RegisterItem
                    date={item[1].data}
                    description={
                      item[1].animalCategoria?.nome || item[1].descricao
                    }
                    category={item[1].rotulo}
                    type={item[0]}
                    canEdit={canEdit}
                    onClick={() =>
                      canEdit
                        ? history.push(
                            `/propriedade/${id_property}/animais/${id_animal}/registros/${item[1].id}/editar`,
                          )
                        : null
                    }
                  />
                ))}

              <SeeMoreButton
                type="button"
                onClick={() =>
                  history.push(
                    `/propriedade/${id_property}/animais/${id_animal}/registros`,
                  )
                }
              >
                Ver todos os registros
              </SeeMoreButton>

              {canEdit && (
                <Button
                  onClick={() =>
                    history.push(
                      `/propriedade/${id_property}/animais/${id_animal}/registros/novo`,
                    )
                  }
                >
                  <IconFilePlus
                    size={30}
                    color="#fff"
                    style={{ marginRight: 10 }}
                  />
                  Novo registro
                </Button>
              )}
            </Section>
          )}

          {selectedTab === 'graph' && (
            <Section>
              <Label>Peso</Label>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart width={500} height={400} data={weights}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis
                      domain={[
                        0,
                        Math.max.apply(
                          Math,
                          weights.map((e: any) => e.weight),
                        ) + 20,
                      ]}
                      unit="kg"
                    />
                    <Tooltip
                      content={props => (
                        <CustomGraphTooltip>
                          <p>
                            {props.payload &&
                              props.payload[0] != null &&
                              props.payload[0].payload.date}
                          </p>
                          <p>
                            Peso:{' '}
                            {props.payload &&
                              props.payload[0] != null &&
                              props.payload[0].payload.weight}
                            kg
                          </p>

                          <p>
                            GMD:{' '}
                            {props.payload &&
                              props.payload[0] != null &&
                              props.payload[0].payload.gmd}
                            kg
                          </p>
                        </CustomGraphTooltip>
                      )}
                    />
                    <Line type="monotone" dataKey="weight" stroke="#0075FF" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          {selectedTab === 'alerts' && (
            <Section>
              <Label>Adicionar alerta</Label>

              <Form ref={alertFormRef} onSubmit={handleAlertChange}>
                <RadioInput
                  direction="row"
                  name="tipo"
                  label="Cor"
                  options={radioOptions}
                  required={true}
                />

                <Input
                  name="titulo"
                  label="Título"
                  defaultValue={
                    animal.alerta_vermelho?.conteudo.split('\n')[0] ||
                    animal.alerta_verde?.conteudo.split('\n')[0]
                  }
                  required={true}
                />

                <TextArea
                  name="descricao"
                  label="Descrição"
                  defaultValue={
                    animal.alerta_vermelho?.conteudo.split('\n')[1] ||
                    animal.alerta_verde?.conteudo.split('\n')[1]
                  }
                  required={true}
                />

                <Button type="submit">Salvar</Button>
              </Form>
            </Section>
          )}

          {animal.imagens?.length > 0 && (
            <ImageModal
              isOpen={isImageModalOpen}
              onClose={() => setIsImageModalOpen(false)}
              data={animal.imagens}
            />
          )}

          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <H2>Alterar estado</H2>
            <Text style={{ marginBottom: 30 }}>
              Adicione um motivo para a alteração de estado do animal, ou deixe
              o campo em branco.
            </Text>

            <Form ref={formRef} onSubmit={handleStateChange}>
              <TextArea
                name="obs"
                label="Descrição"
                defaultValue={animal.obs}
              />

              <Button type="submit" isLoading={loadingAction}>
                Salvar
              </Button>
            </Form>
          </Modal>
        </Content>
      )}
    </Container>
  );
};

export default Animal;
